// loProspectScenarioRules.ts — the enum-side twin of the public preview's
// adaptive field behavior (src/apps/lo-preview/scenarioRules.ts). The internal
// ScenarioRunnerModal speaks raw OB enum values (no display-label layer), so
// its transitions operate on wire values directly. scenarioRulesParity.test.ts
// pins the two implementations against each other so they cannot drift.

/** The modal's form state — OB enum values + string inputs. */
export interface WireScenarioState {
  loanPurpose: string;
  loanType: string;
  productTypes: string[]; // multi-select, like the public preview and Artemis
  loanAmount: string;
  appraisedValue: string;
  purchasePrice: string;
  fico: string;
  dti: string;
  termYears: string;
  lockDays: string;
  compPercent: string;
  targetRate: string;
  propertyState: string;
  propertyZip: string;
  propertyCounty: string;
  propertyType: string;
  occupancy: string;
  cashOutAmount: string;
  cashOutStash: string;
  lienType: string;
  firstLienAmount: string;
  secondLienAmount: string;
  amortizationType: string;
  armFixedTerm: string;
  interestOnly: boolean;
  interestOnlyTermMonths: string;
  aus: string;
  buydown: string;
  borrowerPaidMI: string;
  reducedMI: boolean;
  waiveEscrows: boolean;
  selfEmployed: boolean;
  firstTimeHomeBuyer: boolean;
  citizenship: string;
  monthsReserves: string;
  propertiesFinanced: string;
  numberOfUnits: string;
  numberOfStories: string;
  prepaymentPenalty: string;
  veteranType: string;
  incomeVerificationType: string;
  dscrRatio: string;
  housingEvent: string;
  housingEventSeasoning: string;
  bankruptcy: string;
  bankruptcyOutcome: string;
  bankruptcySeasoning: string;
  mortgageLates: string;
  debtConsolidation: boolean;
  uniqueProperty: boolean;
}

/** OB doc types that force selfEmployed=true — must match the server's
 *  isSelfEmployedIncomeVerificationType in _shared/lo-prospect-ob-request.ts. */
export function isSelfEmployedWireDocType(value: string): boolean {
  if (!value) {
return false;
}
  if (value.includes('BankStmt')) {
return true;
}
  return ['OneYear1099', 'TwoYear1099', 'PLOneYear', 'PLTwoYear'].includes(value);
}

/** A1 — purpose options per loan type, OB enum values. */
export function wirePurposeOptionsForLoanType(loanType: string): string[] {
  const base = ['Purchase', 'RefiRateTermLimitedCO', 'RefiCashout'];
  if (loanType === 'FHA') {
return [...base, 'FHAStreamlineRefi'];
}
  if (loanType === 'VA') {
return [...base, 'VARateReduction'];
}
  return base;
}

type WireValue = string | boolean | string[];

function hasPositiveAmount(value: string): boolean {
  return Number(String(value ?? '').replace(/[^0-9.]/g, '')) > 0;
}

/**
 * Enum-side A1–A9 transition reducer — semantically identical to the public
 * applyScenarioChange, over OB enum values:
 *  A1 loan-type change resets FHAStreamlineRefi / VARateReduction to Rate/Term
 *  A3 self-employed forced ON for bank-stmt/1099/P&L doc types
 *  A4 veteranType cleared when the loan type leaves VA
 *  A5 prepay reset to None when occupancy leaves InvestmentProperty
 *  A6 armFixedTerm reset when amortization returns to Fixed
 *  A7 IO term cleared when interestOnly turns off
 *  A8 cash-out stash/restore as the purpose flips
 *  A9 county cleared when the property state changes
 *  A2 DSCR: selecting InvestorDscr forces the ExpandedGuidelines product type
 */
export function applyWireScenarioChange(
  s: WireScenarioState,
  field: keyof WireScenarioState,
  value: WireValue
): WireScenarioState {
  const next: WireScenarioState = { ...s, [field]: value };

  if (field === 'loanType') {
    const v = value as string;
    if (v !== 'FHA' && next.loanPurpose === 'FHAStreamlineRefi') {
      next.loanPurpose = 'RefiRateTermLimitedCO';
    }
    if (v !== 'VA' && next.loanPurpose === 'VARateReduction') {
      next.loanPurpose = 'RefiRateTermLimitedCO';
    }
    if (v !== 'VA' && next.veteranType) {
      next.veteranType = '';
    }
  }

  if (field === 'loanPurpose') {
    const leavingCashOut = s.loanPurpose === 'RefiCashout' && value !== 'RefiCashout';
    const enteringCashOut = s.loanPurpose !== 'RefiCashout' && value === 'RefiCashout';
    if (leavingCashOut) {
      if (hasPositiveAmount(s.cashOutAmount)) {
        next.cashOutStash = s.cashOutAmount;
      }
      next.cashOutAmount = '0';
    } else if (enteringCashOut) {
      next.cashOutAmount = hasPositiveAmount(s.cashOutStash) ? s.cashOutStash : '';
    }
  }

  if (field === 'amortizationType' && value !== 'ARM') {
    next.armFixedTerm = 'SevenYear';
  }

  if (field === 'interestOnly' && value !== true) {
    next.interestOnlyTermMonths = '';
  }

  if (field === 'occupancy' && value !== 'InvestmentProperty' && next.prepaymentPenalty !== 'None') {
    next.prepaymentPenalty = 'None';
  }

  if (field === 'incomeVerificationType') {
    if (isSelfEmployedWireDocType(value as string)) {
      next.selfEmployed = true;
    }
    if (value === 'InvestorDscr' && !next.productTypes.includes('ExpandedGuidelines')) {
      next.productTypes = [...next.productTypes, 'ExpandedGuidelines'];
    }
  }

  if (field === 'propertyState' && value !== s.propertyState) {
    next.propertyCounty = '';
  }

  return next;
}
